import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AppRouter from "./AppRouter";
import type { AppDispatch, RootState } from "./app/redux/store";
import { loadCountries } from "./app/redux/reducers/countrySlice";
import { setLanguage } from "./app/redux/reducers/settingsSlice";
import { getEnvConfig } from "./app/apiConfig/urlConfig/envConfig";

const App = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { i18n } = useTranslation();

  const selectedLanguage = useSelector(
    (state: RootState) => state.settings.language,
  );

  const countriesLoaded = useSelector(
    (state: RootState) => state.countries.data.length > 0,
  );

  // ......................getEnvConfig..................................
  const envConfig = getEnvConfig();

  // -------------------- Load Countries --------------------
  useEffect(() => {
    if (!countriesLoaded) {
      dispatch(loadCountries());
    }
  }, [dispatch, countriesLoaded]);

  // -------------------- Load Saved Language --------------------
  useEffect(() => {
    const savedLanguage = localStorage.getItem("appLanguage");
    if (savedLanguage && savedLanguage !== selectedLanguage) {
      dispatch(setLanguage(savedLanguage));
    }
  }, [dispatch]);

  // -------------------- Sync i18n Language --------------------
  useEffect(() => {
    const langMap: Record<string, string> = {
      English: "en",
      Arabic: "ar",
      Spanish: "es",
      Catalan: "ca",
      Portuguese: "pt",
    };
    const langCode = langMap[selectedLanguage] || "en";
    if (langCode !== i18n.language) {
      i18n.changeLanguage(langCode);
    }
  }, [selectedLanguage, i18n]);

  // -------------------- Handle RTL --------------------
  useEffect(() => {
    const rtlLanguages = ["ar", "he", "fa"];
    const isRtl =
      rtlLanguages.includes(i18n.language) || i18n.dir(i18n.language) === "rtl";
    const currentDir = isRtl ? "rtl" : "ltr";
    document.documentElement.dir = currentDir;
    document.documentElement.lang = i18n.language;
    document.body.dir = currentDir;
  }, [i18n.language]);

  // -------------------- Apply Theme Variables --------------------
  useEffect(() => {
    const theme = envConfig?.theme;

    if (!theme) return;
    Object.entries(theme).forEach(([key, value]) => {
      if (typeof value === "string") {
        document.documentElement.style.setProperty(`--${key}`, value);
      }
    });
  }, []);

  return <AppRouter />;
};

export default App;
